import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardActionArea,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Description as NFeIcon,
  QrCodeScanner as BarcodeIcon,
  EditNote as ManualIcon,
  ContentCopy as DuplicateIcon,
  AttachMoney as PayNowIcon,
  Discount as DiscountIcon,
  Receipt as ReceiptIcon,
  AttachFile as AttachIcon,
  AccountBalance as BankIcon,
  Business as SupplierIcon,
  CalendarMonth as CalendarIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';
import { useLocation, useNavigate } from 'react-router-dom';

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const formatDate = (date: string) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

interface AccountPayable {
  id: string;
  description: string;
  category: string;
  amount: number;
  amount_paid: number;
  payment_method: string;
  due_date: string;
  status: string;
  notes?: string;
  supplier_name?: string;
  supplier_cnpj?: string;
  nfe_number?: string;
  nfe_key?: string;
  bank_account?: string;
  cost_center?: string;
  buyer?: string;
  project?: string;
  installment_number?: number;
  installment_total?: number;
  installments?: Installment[];
  payments?: PaymentRecord[];
  attachments?: Attachment[];
}

interface Installment {
  id?: string;
  number: number;
  due_date: string;
  amount: number;
  percentage: number;
  status: string;
}

interface PaymentRecord {
  id?: string;
  date: string;
  amount: number;
  discount: number;
  interest: number;
  penalty: number;
  bank: string;
  notes?: string;
}

interface Attachment {
  id?: string;
  name: string;
  url?: string;
  file_url?: string;
  type: string;
  size?: number;
  uploaded_at: string;
}

type EntryMode = 'manual' | 'nfe' | 'boleto';

interface FixedExpense {
  id: string;
  description: string;
  category: string;
  amount: number;
  frequency: string;
  due_day: number;
  supplier_name?: string;
  is_active: boolean;
  notes?: string;
}

const CATEGORIES = [
  { value: 'insumos', label: 'Insumos' },
  { value: 'materiais', label: 'Materiais e Equipamentos' },
  { value: 'medicamentos', label: 'Medicamentos' },
  { value: 'abastecimentos', label: 'Abastecimentos' },
  { value: 'alimentacao', label: 'Alimentação' },
  { value: 'aluguel', label: 'Aluguel' },
  { value: 'conta_internet', label: 'Conta de Internet' },
  { value: 'conta_luz', label: 'Conta de Luz' },
  { value: 'conta_telefone', label: 'Conta de Telefone' },
  { value: 'contas', label: 'Contas' },
  { value: 'credito', label: 'Crédito' },
  { value: 'despesas', label: 'Despesas' },
  { value: 'devolucoes', label: 'Devoluções' },
  { value: 'passagem', label: 'Passagem' },
  { value: 'patrimonio', label: 'Patrimônio' },
  { value: 'repasses', label: 'Repasses' },
  { value: 'repasses_comissoes', label: 'Repasses e Comissões' },
  { value: 'salarios', label: 'Salários' },
  { value: 'seguros', label: 'Seguros' },
  { value: 'servicos', label: 'Serviços Terceirizados' },
  { value: 'telefonia', label: 'Telefonia' },
  { value: 'tributos', label: 'Tributos e Impostos' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'limpeza', label: 'Limpeza e Higienização' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Dinheiro' },
  { value: 'credit_card', label: 'Cartão de Crédito' },
  { value: 'debit_card', label: 'Cartão de Débito' },
  { value: 'pix', label: 'PIX' },
  { value: 'bank_transfer', label: 'Transferência Bancária' },
  { value: 'check', label: 'Cheque' },
  { value: 'boleto', label: 'Boleto Bancário' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'a_vencer', label: 'A Vencer' },
  { value: 'pago', label: 'Pago' },
  { value: 'atrasado', label: 'Atrasado' },
  { value: 'cancelado', label: 'Cancelado' },
];

const COST_CENTERS = [
  { value: 'COM/ESTOQUE', label: 'COM/ESTOQUE' },
  { value: 'ADM/GERAL', label: 'ADM/GERAL' },
  { value: 'CLINICA/PRINCIPAL', label: 'CLÍNICA/PRINCIPAL' },
  { value: 'CLINICA/FILIAL', label: 'CLÍNICA/FILIAL' },
  { value: 'COM/IMOBILIZADO', label: 'COM/IMOBILIZADO' },
  { value: 'MKT', label: 'Marketing' },
  { value: 'TI', label: 'T.I.' },
  { value: 'RH', label: 'Recursos Humanos' },
];

const FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Mensal' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'biweekly', label: 'Quinzenal' },
  { value: 'yearly', label: 'Anual' },
];

const getStatusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  switch (status) {
    case 'pago': return 'success';
    case 'pendente': case 'a_vencer': return 'warning';
    case 'atrasado': return 'error';
    case 'cancelado': return 'default';
    default: return 'info';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'a_vencer': return 'A Vencer';
    case 'pago': return 'Pago';
    case 'pendente': return 'Pendente';
    case 'atrasado': return 'Atrasado';
    case 'cancelado': return 'Cancelado';
    default: return status;
  }
};

const getCategoryLabel = (value: string) =>
  CATEGORIES.find((c) => c.value === value)?.label || value;

const getPaymentMethodLabel = (value: string) =>
  PAYMENT_METHODS.find((m) => m.value === value)?.label || value;

const getFrequencyLabel = (value: string) =>
  FREQUENCY_OPTIONS.find((f) => f.value === value)?.label || value;

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

const emptyAccountForm = () => ({
  description: '',
  category: '',
  amount: '',
  payment_method: '',
  due_date: '',
  status: 'pendente',
  notes: '',
  supplier_name: '',
  supplier_cnpj: '',
  nfe_number: '',
  nfe_key: '',
  bank_account: '',
  cost_center: '',
  buyer: '',
  project: '',
  num_installments: '1',
  barcode: '',
});

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return <ImageIcon sx={{ color: '#8b5cf6' }} />;
  if (type === 'application/pdf') return <PdfIcon sx={{ color: '#dc2626' }} />;
  return <FileIcon sx={{ color: '#6b7280' }} />;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

/** Boleto linha digitável embutida em `notes` — a tabela pode não ter coluna `barcode` (PostgREST PGRST204). */
const AP_BARCODE_START = '[[AP_BARCODE]]';
const AP_BARCODE_END = '[[/AP_BARCODE]]';

function splitBarcodeFromNotes(notes: string | undefined): { cleanNotes: string; barcode: string } {
  if (!notes?.trim()) return { cleanNotes: '', barcode: '' };
  const s = notes.indexOf(AP_BARCODE_START);
  if (s === -1) return { cleanNotes: notes.trim(), barcode: '' };
  const e = notes.indexOf(AP_BARCODE_END, s);
  if (e === -1) return { cleanNotes: notes.trim(), barcode: '' };
  const inner = notes.slice(s + AP_BARCODE_START.length, e).replace(/^\n+/, '').replace(/\n+$/, '');
  const before = notes.slice(0, s).trimEnd();
  const after = notes.slice(e + AP_BARCODE_END.length).replace(/^\n+/, '');
  const cleanNotes = [before, after].filter(Boolean).join('\n\n').trim();
  return { cleanNotes, barcode: inner.trim() };
}

function mergeBarcodeIntoNotes(notes: string, barcode: string, mode: EntryMode): string | undefined {
  const { cleanNotes } = splitBarcodeFromNotes(notes || '');
  if (mode === 'boleto' && barcode.trim()) {
    const block = `${AP_BARCODE_START}\n${barcode.trim()}\n${AP_BARCODE_END}`;
    return cleanNotes ? `${cleanNotes}\n\n${block}` : block;
  }
  return cleanNotes || undefined;
}

/** Metadados em [[AP_EXT]]; upload via POST /financial-module/upload (multer → R2, aceita qualquer arquivo). */
interface ApStoredAttachment {
  id: string;
  name: string;
  url: string;
  file_url?: string;
  type: string;
  size?: number;
  uploaded_at: string;
}

/** Campos Omie-style que podem não existir em clinic_financial_transactions (PGRST204). */
interface ApExtPayload {
  cost_center?: string;
  buyer?: string;
  project?: string;
  bank_account?: string;
  nfe_number?: string;
  nfe_key?: string;
  installments?: Installment[];
  file_attachments?: ApStoredAttachment[];
}

const AP_EXT_START = '[[AP_EXT]]';
const AP_EXT_END = '[[/AP_EXT]]';

function extractExtBlock(full: string): { inner: string; withoutExt: string } {
  if (!full?.trim()) return { inner: '', withoutExt: full || '' };
  const s = full.indexOf(AP_EXT_START);
  if (s === -1) return { inner: '', withoutExt: full };
  const e = full.indexOf(AP_EXT_END, s + AP_EXT_START.length);
  if (e === -1) return { inner: '', withoutExt: full };
  const inner = full.slice(s + AP_EXT_START.length, e).trim();
  const before = full.slice(0, s).trimEnd();
  const after = full.slice(e + AP_EXT_END.length).replace(/^\n+/, '');
  const withoutExt = [before, after].filter(Boolean).join('\n\n').trim();
  return { inner, withoutExt };
}

function pruneApExt(ext: ApExtPayload): ApExtPayload {
  const out: ApExtPayload = {};
  if (ext.cost_center?.trim()) out.cost_center = ext.cost_center.trim();
  if (ext.buyer?.trim()) out.buyer = ext.buyer.trim();
  if (ext.project?.trim()) out.project = ext.project.trim();
  if (ext.bank_account?.trim()) out.bank_account = ext.bank_account.trim();
  if (ext.nfe_number?.trim()) out.nfe_number = ext.nfe_number.trim();
  if (ext.nfe_key?.trim()) out.nfe_key = ext.nfe_key.trim().replace(/\D/g, '');
  if (ext.installments && ext.installments.length > 1) out.installments = ext.installments;
  if (ext.file_attachments && ext.file_attachments.length > 0) {
    out.file_attachments = ext.file_attachments
      .filter((a) => a && (a.url || a.file_url) && a.name)
      .map((a) => ({
        id: String(a.id || ''),
        name: a.name,
        url: a.url || a.file_url || '',
        file_url: a.file_url || a.url,
        type: a.type || 'application/octet-stream',
        size: a.size,
        uploaded_at: a.uploaded_at || new Date().toISOString(),
      }));
  }
  return out;
}

function extFileAttachmentsToAttachments(items: ApStoredAttachment[] | undefined): Attachment[] {
  if (!items?.length) return [];
  return items.map((a) => ({
    id: a.id,
    name: a.name,
    url: a.url,
    file_url: a.file_url || a.url,
    type: a.type,
    size: a.size,
    uploaded_at: a.uploaded_at,
  }));
}

/** Respostas: galeria clínica `{ success, data: { file_url } }`; outros `{ data: { url } }`. */
function parseClinicUploadFileUrl(data: unknown): string {
  if (!data || typeof data !== 'object') return '';
  const d = data as Record<string, unknown>;
  if (d.success === false) return '';
  const inner = d.data as Record<string, unknown> | undefined;
  if (inner && typeof inner.file_url === 'string' && inner.file_url) return inner.file_url;
  if (inner && typeof inner.url === 'string' && inner.url) return inner.url;
  if (typeof d.file_url === 'string' && d.file_url) return d.file_url;
  if (typeof d.url === 'string' && d.url) return d.url;
  return '';
}

function newAttachmentId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function parseAccountPayableNotes(fullNotes: string): {
  cleanNotes: string;
  barcode: string;
  ext: ApExtPayload;
} {
  const { inner, withoutExt } = extractExtBlock(fullNotes || '');
  let ext: ApExtPayload = {};
  if (inner) {
    try {
      ext = JSON.parse(inner) as ApExtPayload;
    } catch {
      /* JSON inválido em notas antigas */
    }
  }
  const { cleanNotes, barcode } = splitBarcodeFromNotes(withoutExt);
  return { cleanNotes, barcode, ext };
}

function serializeAccountPayableNotes(
  cleanNotes: string,
  barcode: string,
  ext: ApExtPayload,
  entryMode: EntryMode
): string | undefined {
  const extPruned = pruneApExt(ext);
  const withBarcode = mergeBarcodeIntoNotes(cleanNotes || '', barcode, entryMode) || '';
  if (Object.keys(extPruned).length === 0) {
    return withBarcode || undefined;
  }
  const json = JSON.stringify(extPruned);
  const extBlock = `${AP_EXT_START}\n${json}\n${AP_EXT_END}`;
  return withBarcode ? `${withBarcode}\n\n${extBlock}` : extBlock;
}

function resolvePayableNfeNumber(a: Pick<AccountPayable, 'nfe_number' | 'notes'>): string {
  if (a.nfe_number) return a.nfe_number;
  return parseAccountPayableNotes(a.notes || '').ext.nfe_number || '';
}

const AccountsPayable: React.FC = () => {
  const { isMobile } = usePageBreakpoints();
  const location = useLocation();
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);

  const [accounts, setAccounts] = useState<AccountPayable[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsTotal, setAccountsTotal] = useState(0);
  const [accountsPage, setAccountsPage] = useState(0);
  const [accountsRowsPerPage, setAccountsRowsPerPage] = useState(10);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountStatusFilter, setAccountStatusFilter] = useState('');
  const [accountDateFrom, setAccountDateFrom] = useState('');
  const [accountDateTo, setAccountDateTo] = useState('');

  const [wizardOpen, setWizardOpen] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<AccountPayable | null>(null);
  const [accountForm, setAccountForm] = useState(emptyAccountForm());
  const [detailTabIndex, setDetailTabIndex] = useState(0);
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [entryMode, setEntryMode] = useState<EntryMode>('manual');

  const [selectedAccount, setSelectedAccount] = useState<AccountPayable | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', discount: '0', interest: '0', penalty: '0', bank: '', notes: '', date: new Date().toISOString().slice(0, 10) });

  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountForm, setDiscountForm] = useState({ amount: '', notes: '' });

  const [fixedExpenses, setFixedExpenses] = useState<FixedExpense[]>([]);
  const [fixedLoading, setFixedLoading] = useState(true);
  const [fixedDialogOpen, setFixedDialogOpen] = useState(false);
  const [editingFixed, setEditingFixed] = useState<FixedExpense | null>(null);
  const [fixedForm, setFixedForm] = useState({
    description: '', category: '', amount: '', frequency: 'monthly',
    due_day: '', supplier_name: '', is_active: true, notes: '',
  });

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const [nfeLookupLoading, setNfeLookupLoading] = useState(false);
  const [nfeLookupError, setNfeLookupError] = useState<string | null>(null);

  const buildApExtPayload = (atts: Attachment[]): ApExtPayload => ({
    cost_center: accountForm.cost_center || undefined,
    buyer: accountForm.buyer || undefined,
    project: accountForm.project || undefined,
    bank_account: accountForm.bank_account || undefined,
    nfe_number: accountForm.nfe_number || undefined,
    nfe_key: accountForm.nfe_key || undefined,
    installments: installments.length > 1 ? installments : undefined,
    file_attachments:
      atts.length > 0
        ? atts.map((a) => ({
            id: String(a.id || newAttachmentId()),
            name: a.name,
            url: (a.file_url || a.url || '').trim(),
            file_url: a.file_url || a.url,
            type: a.type || 'application/octet-stream',
            size: a.size,
            uploaded_at: a.uploaded_at || new Date().toISOString(),
          }))
        : undefined,
  });

  const persistPayableNotesOnly = async (accountId: string, atts: Attachment[]) => {
    const notesPayload = serializeAccountPayableNotes(
      accountForm.notes,
      accountForm.barcode,
      buildApExtPayload(atts),
      entryMode
    );
    await axios.put(getApiUrl(`/financial-module/accounts-payable/${accountId}`), { notes: notesPayload });
  };

  const loadAccounts = async () => {
    setAccountsLoading(true);
    try {
      const params: Record<string, string | number> = { page: accountsPage + 1, per_page: accountsRowsPerPage };
      if (accountSearch) params.search = accountSearch;
      if (accountStatusFilter) params.status = accountStatusFilter;
      if (accountDateFrom) params.date_from = accountDateFrom;
      if (accountDateTo) params.date_to = accountDateTo;
      const response = await axios.get(getApiUrl('/financial-module/accounts-payable'), { params });
      const resData = response.data;
      setAccounts(resData.data || resData.items || resData || []);
      setAccountsTotal(resData.total ?? resData.pagination?.total ?? (resData.data?.length || 0));
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao carregar contas a pagar', severity: 'error' });
    } finally {
      setAccountsLoading(false);
    }
  };

  useEffect(() => { loadAccounts(); }, [accountsPage, accountsRowsPerPage]);

  /** Consulta externa ao completar 44 dígitos (nova conta no modo NF-e; não sobrescreve na edição). */
  useEffect(() => {
    if (entryMode !== 'nfe' || !accountDialogOpen || editingAccount) {
      setNfeLookupLoading(false);
      setNfeLookupError(null);
      return;
    }
    const chave = (accountForm.nfe_key || '').replace(/\D/g, '');
    if (chave.length !== 44) {
      setNfeLookupLoading(false);
      setNfeLookupError(null);
      return;
    }
    const ac = new AbortController();
    const tid = window.setTimeout(() => {
      (async () => {
        setNfeLookupLoading(true);
        setNfeLookupError(null);
        try {
          const response = await axios.get(getApiUrl('/financial-module/nfe-recebida-lookup'), {
            params: { chave },
            signal: ac.signal,
          });
          if (ac.signal.aborted) return;
          const payload = response.data?.data as {
            nfe_number?: string;
            supplier_name?: string;
            supplier_cnpj?: string;
            description?: string;
            amount?: number;
            due_date?: string;
          } | undefined;
          if (!payload) {
            setNfeLookupError('Resposta vazia da API de NF-e');
            return;
          }
          setAccountForm((prev) => ({
            ...prev,
            nfe_number: payload.nfe_number || prev.nfe_number,
            supplier_name: payload.supplier_name || prev.supplier_name,
            supplier_cnpj: payload.supplier_cnpj || prev.supplier_cnpj,
            description: payload.description || prev.description,
            amount:
              payload.amount !== undefined && Number.isFinite(Number(payload.amount))
                ? String(payload.amount)
                : prev.amount,
            due_date: payload.due_date || prev.due_date,
          }));
        } catch (err: unknown) {
          if (axios.isCancel(err)) return;
          const ax = err as { code?: string; response?: { data?: { error?: string } } };
          if (ax?.code === 'ERR_CANCELED') return;
          const msg =
            ax?.response?.data?.error ||
            (err instanceof Error ? err.message : null) ||
            'Não foi possível consultar a NF-e';
          setNfeLookupError(String(msg));
        } finally {
          if (!ac.signal.aborted) setNfeLookupLoading(false);
        }
      })();
    }, 480);
    return () => {
      window.clearTimeout(tid);
      ac.abort();
    };
  }, [accountForm.nfe_key, entryMode, accountDialogOpen, editingAccount]);

  const handleAccountSearch = () => { setAccountsPage(0); loadAccounts(); };

  const generateInstallments = (total: number, count: number, firstDue: string) => {
    if (!total || count < 1 || !firstDue) return [];
    const perInstallment = Math.round((total / count) * 100) / 100;
    const pct = Math.round((100 / count) * 100) / 100;
    const items: Installment[] = [];
    for (let i = 0; i < count; i++) {
      const d = new Date(firstDue);
      d.setMonth(d.getMonth() + i);
      const isLast = i === count - 1;
      items.push({
        number: i + 1,
        due_date: d.toISOString().slice(0, 10),
        amount: isLast ? Math.round((total - perInstallment * (count - 1)) * 100) / 100 : perInstallment,
        percentage: isLast ? Math.round((100 - pct * (count - 1)) * 100) / 100 : pct,
        status: 'pendente',
      });
    }
    return items;
  };

  const openNewAccount = (mode: EntryMode) => {
    setWizardOpen(false);
    setEditingAccount(null);
    setNfeLookupError(null);
    setNfeLookupLoading(false);
    setEntryMode(mode);
    const form = emptyAccountForm();
    if (mode === 'nfe') {
      form.category = 'insumos';
    }
    if (mode === 'boleto') {
      form.payment_method = 'boleto';
    }
    setAccountForm(form);
    setInstallments([]);
    setAttachments([]);
    setDetailTabIndex(0);
    setAccountDialogOpen(true);
  };

  const openEditAccount = (account: AccountPayable) => {
    setEditingAccount(account);
    setNfeLookupError(null);
    setNfeLookupLoading(false);
    const nfeResolved = resolvePayableNfeNumber(account);
    const mode: EntryMode = nfeResolved ? 'nfe' : account.payment_method === 'boleto' ? 'boleto' : 'manual';
    setEntryMode(mode);
    const parsed = parseAccountPayableNotes(account.notes || '');
    const { ext } = parsed;
    setAccountForm({
      description: account.description,
      category: account.category,
      amount: account.amount.toString(),
      payment_method: account.payment_method,
      due_date: account.due_date,
      status: account.status,
      notes: parsed.cleanNotes,
      supplier_name: account.supplier_name || '',
      supplier_cnpj: account.supplier_cnpj || '',
      nfe_number: account.nfe_number || ext.nfe_number || '',
      nfe_key: account.nfe_key || ext.nfe_key || '',
      bank_account: account.bank_account || ext.bank_account || '',
      cost_center: account.cost_center || ext.cost_center || '',
      buyer: account.buyer || ext.buyer || '',
      project: account.project || ext.project || '',
      num_installments: String(account.installment_total || 1),
      barcode: parsed.barcode,
    });
    setInstallments(
      account.installments && account.installments.length > 0
        ? account.installments
        : ext.installments || []
    );
    setAttachments(
      account.attachments && account.attachments.length > 0
        ? account.attachments
        : extFileAttachmentsToAttachments(ext.file_attachments)
    );
    setDetailTabIndex(0);
    setAccountDialogOpen(true);
  };

  const openEditAccountOnTab = (account: AccountPayable, tab: number) => {
    openEditAccount(account);
    setTimeout(() => setDetailTabIndex(tab), 50);
  };

  const handleSaveAccount = async () => {
    if (!accountForm.description || !accountForm.amount || !accountForm.due_date) {
      setSnackbar({ open: true, message: 'Preencha Descrição, Valor e Vencimento', severity: 'error' });
      return;
    }
    try {
      const notesPayload = serializeAccountPayableNotes(
        accountForm.notes,
        accountForm.barcode,
        buildApExtPayload(attachments),
        entryMode
      );
      const payload = {
        description: accountForm.description,
        category: accountForm.category || undefined,
        amount: parseFloat(accountForm.amount),
        payment_method: accountForm.payment_method || undefined,
        due_date: accountForm.due_date,
        status: accountForm.status,
        notes: notesPayload,
        supplier_name: accountForm.supplier_name || undefined,
        supplier_cnpj: accountForm.supplier_cnpj || undefined,
      };
      if (editingAccount) {
        await axios.put(getApiUrl(`/financial-module/accounts-payable/${editingAccount.id}`), payload);
        setSnackbar({ open: true, message: 'Conta atualizada com sucesso', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/financial-module/accounts-payable'), payload);
        setSnackbar({ open: true, message: 'Conta criada com sucesso', severity: 'success' });
      }
      setAccountDialogOpen(false);
      setEditingAccount(null);
      loadAccounts();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao salvar conta', severity: 'error' });
    }
  };

  const handleDeleteAccount = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta conta?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/accounts-payable/${id}`));
      setSnackbar({ open: true, message: 'Conta excluída com sucesso', severity: 'success' });
      setSelectedAccount(null);
      setDrawerOpen(false);
      loadAccounts();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao excluir conta', severity: 'error' });
    }
  };

  const handleDuplicate = (acc: AccountPayable) => {
    const parsed = parseAccountPayableNotes(acc.notes || '');
    const { ext } = parsed;
    const form = emptyAccountForm();
    form.description = acc.description + ' (cópia)';
    form.category = acc.category;
    form.amount = acc.amount.toString();
    form.payment_method = acc.payment_method;
    form.supplier_name = acc.supplier_name || '';
    form.supplier_cnpj = acc.supplier_cnpj || '';
    form.cost_center = acc.cost_center || ext.cost_center || '';
    form.buyer = acc.buyer || ext.buyer || '';
    form.project = acc.project || ext.project || '';
    form.bank_account = acc.bank_account || ext.bank_account || '';
    form.nfe_number = '';
    form.nfe_key = '';
    form.notes = parsed.cleanNotes;
    form.barcode = parsed.barcode;
    const hadNfe = resolvePayableNfeNumber(acc);
    const mode: EntryMode = hadNfe ? 'nfe' : acc.payment_method === 'boleto' ? 'boleto' : 'manual';
    setEntryMode(mode);
    setEditingAccount(null);
    setAccountForm(form);
    setInstallments([]);
    setAttachments([]);
    setDetailTabIndex(0);
    setAccountDialogOpen(true);
    setDrawerOpen(false);
  };

  const handlePayNow = async () => {
    if (!selectedAccount || !payForm.amount) return;
    try {
      const payload = {
        status: 'pago',
        amount_paid: parseFloat(payForm.amount),
        payment_date: payForm.date,
        discount: parseFloat(payForm.discount) || 0,
        interest: parseFloat(payForm.interest) || 0,
        penalty: parseFloat(payForm.penalty) || 0,
        payment_bank: payForm.bank || undefined,
        payment_notes: payForm.notes || undefined,
      };
      await axios.put(getApiUrl(`/financial-module/accounts-payable/${selectedAccount.id}`), payload);
      setSnackbar({ open: true, message: 'Pagamento registrado — status alterado para PAGO', severity: 'success' });
      setPayDialogOpen(false);
      setDrawerOpen(false);
      setSelectedAccount(null);
      loadAccounts();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao registrar pagamento', severity: 'error' });
    }
  };

  const handleRegisterDiscount = async () => {
    if (!selectedAccount || !discountForm.amount) return;
    try {
      await axios.put(getApiUrl(`/financial-module/accounts-payable/${selectedAccount.id}`), {
        discount: parseFloat(discountForm.amount),
        discount_notes: discountForm.notes || undefined,
      });
      setSnackbar({ open: true, message: 'Desconto registrado com sucesso', severity: 'success' });
      setDiscountDialogOpen(false);
      loadAccounts();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao registrar desconto', severity: 'error' });
    }
  };

  const handleUploadAttachment = async (files: FileList | File[]) => {
    const accountId = editingAccount?.id;
    if (!accountId) {
      setSnackbar({ open: true, message: 'Salve a conta antes de anexar arquivos', severity: 'error' });
      return;
    }
    let nextList = [...attachments];
    for (const file of Array.from(files)) {
      setUploadingFile(true);
      setUploadProgress(0);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(getApiUrl('/financial-module/upload'), formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
            }
          },
        });
        const rd = response.data as { success?: boolean; error?: string };
        if (rd && typeof rd === 'object' && rd.success === false) {
          setSnackbar({ open: true, message: rd.error || 'Upload recusado pela API', severity: 'error' });
          continue;
        }
        const fileUrl = parseClinicUploadFileUrl(response.data);
        if (!fileUrl) {
          setSnackbar({
            open: true,
            message: (response.data as { error?: string })?.error || 'API não retornou URL do arquivo (file_url)',
            severity: 'error',
          });
          continue;
        }
        const uploaded: Attachment = {
          id: newAttachmentId(),
          name: file.name,
          url: fileUrl,
          file_url: fileUrl,
          type: file.type || 'application/octet-stream',
          size: file.size,
          uploaded_at: new Date().toISOString(),
        };
        nextList = [...nextList, uploaded];
        setAttachments(nextList);
        await persistPayableNotesOnly(accountId, nextList);
        setSnackbar({ open: true, message: `"${file.name}" anexado com sucesso`, severity: 'success' });
      } catch (error: any) {
        setSnackbar({
          open: true,
          message: error.response?.data?.error || error.response?.data?.message || `Erro ao enviar "${file.name}"`,
          severity: 'error',
        });
      } finally {
        setUploadingFile(false);
        setUploadProgress(0);
      }
    }
  };

  const handleDeleteAttachment = async (attachment: Attachment) => {
    if (!editingAccount?.id || !attachment.id) return;
    if (!window.confirm(`Excluir anexo "${attachment.name}"?`)) return;
    try {
      const next = attachments.filter((a) => a.id !== attachment.id);
      setAttachments(next);
      await persistPayableNotesOnly(editingAccount.id, next);
      setSnackbar({ open: true, message: 'Anexo excluído', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao excluir anexo', severity: 'error' });
    }
  };

  const handleDownloadAttachment = (attachment: Attachment) => {
    const url = attachment.file_url || attachment.url;
    if (url) window.open(url, '_blank');
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      void handleUploadAttachment(e.dataTransfer.files);
    }
  };

  const handleSelectRow = (acc: AccountPayable) => {
    setSelectedAccount(acc);
    if (isMobile) {
      setDrawerOpen(true);
    }
  };

  // Fixed Expenses
  const loadFixedExpenses = async () => {
    setFixedLoading(true);
    try {
      const response = await axios.get(getApiUrl('/financial-module/fixed-expenses'));
      const resData = response.data;
      setFixedExpenses(resData.data || resData.items || resData || []);
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao carregar despesas fixas', severity: 'error' });
    } finally { setFixedLoading(false); }
  };

  useEffect(() => { if (tabIndex === 1) loadFixedExpenses(); }, [tabIndex]);
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setTabIndex(p.get('tab') === 'fixed' ? 1 : 0);
  }, [location.search]);

  const handleChangeTab = (_: React.SyntheticEvent, n: number) => {
    setTabIndex(n);
    navigate(n === 1 ? '/financial/accounts-payable?tab=fixed' : '/financial/accounts-payable');
  };

  const openFixedDialog = (expense?: FixedExpense) => {
    if (expense) {
      setEditingFixed(expense);
      setFixedForm({ description: expense.description, category: expense.category, amount: expense.amount.toString(), frequency: expense.frequency, due_day: expense.due_day.toString(), supplier_name: expense.supplier_name || '', is_active: expense.is_active, notes: expense.notes || '' });
    } else {
      setEditingFixed(null);
      setFixedForm({ description: '', category: '', amount: '', frequency: 'monthly', due_day: '', supplier_name: '', is_active: true, notes: '' });
    }
    setFixedDialogOpen(true);
  };

  const handleSaveFixed = async () => {
    if (!fixedForm.description || !fixedForm.amount || !fixedForm.due_day) {
      setSnackbar({ open: true, message: 'Preencha os campos obrigatórios', severity: 'error' }); return;
    }
    try {
      const payload = { description: fixedForm.description, category: fixedForm.category || undefined, amount: parseFloat(fixedForm.amount), frequency: fixedForm.frequency, due_day: parseInt(fixedForm.due_day, 10), supplier_name: fixedForm.supplier_name || undefined, is_active: fixedForm.is_active, notes: fixedForm.notes || undefined };
      if (editingFixed) {
        await axios.put(getApiUrl(`/financial-module/fixed-expenses/${editingFixed.id}`), payload);
        setSnackbar({ open: true, message: 'Despesa fixa atualizada', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/financial-module/fixed-expenses'), payload);
        setSnackbar({ open: true, message: 'Despesa fixa criada', severity: 'success' });
      }
      setFixedDialogOpen(false);
      setEditingFixed(null);
      loadFixedExpenses();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao salvar despesa fixa', severity: 'error' });
    }
  };

  const handleDeleteFixed = async (id: string) => {
    if (!window.confirm('Excluir esta despesa fixa?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/fixed-expenses/${id}`));
      setSnackbar({ open: true, message: 'Despesa fixa excluída', severity: 'success' });
      loadFixedExpenses();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao excluir', severity: 'error' });
    }
  };

  const summaryTotals = useMemo(() => {
    const total = accounts.reduce((s, a) => s + (a.amount || 0), 0);
    const paid = accounts.reduce((s, a) => s + (a.amount_paid || 0), 0);
    return { total, paid };
  }, [accounts]);

  const sidebarActions = [
    { icon: <EditIcon fontSize="small" />, label: 'Editar', action: () => selectedAccount && openEditAccount(selectedAccount) },
    { icon: <DuplicateIcon fontSize="small" />, label: 'Duplicar', action: () => selectedAccount && handleDuplicate(selectedAccount) },
    { icon: <PayNowIcon fontSize="small" />, label: 'Pagar Agora', color: '#16a34a', action: () => {
      if (!selectedAccount) return;
      setPayForm({ amount: selectedAccount.amount.toString(), discount: '0', interest: '0', penalty: '0', bank: '', notes: '', date: new Date().toISOString().slice(0, 10) });
      setPayDialogOpen(true);
    }},
    { icon: <DiscountIcon fontSize="small" />, label: 'Registrar Desconto', action: () => {
      setDiscountForm({ amount: '', notes: '' });
      setDiscountDialogOpen(true);
    }},
    { icon: <ReceiptIcon fontSize="small" />, label: 'Gerar Recibo', action: () => setSnackbar({ open: true, message: 'Recibo gerado (simulação)', severity: 'success' }) },
    { icon: <NFeIcon fontSize="small" />, label: 'Exibir DANFE', action: () => {
      const nf = selectedAccount ? resolvePayableNfeNumber(selectedAccount) : '';
      setSnackbar({ open: true, message: nf ? `DANFE ${nf}` : 'Sem NF-e vinculada', severity: 'success' });
    }},
    { icon: <AttachIcon fontSize="small" />, label: 'Anexos', action: () => { if (selectedAccount) openEditAccountOnTab(selectedAccount, 4); setDrawerOpen(false); } },
    { icon: <DeleteIcon fontSize="small" />, label: 'Excluir', color: '#dc2626', action: () => selectedAccount && handleDeleteAccount(selectedAccount.id) },
  ];

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon sx={{ fontSize: 32, color: '#16a34a' }} />
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold' }}>Contas a Pagar</Typography>
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs value={tabIndex} onChange={handleChangeTab} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Listar" />
          <Tab label="Despesas Fixas" />
        </Tabs>

        {/* ── Tab 0 ── */}
        <TabPanel value={tabIndex} index={0}>
          <Box sx={{ px: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField label="Buscar" fullWidth size="small" value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAccountSearch()}
                  InputProps={{ endAdornment: <IconButton size="small" onClick={handleAccountSearch}><SearchIcon fontSize="small" /></IconButton> }} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={accountStatusFilter} onChange={(e) => { setAccountStatusFilter(e.target.value); setAccountsPage(0); }} label="Status">
                    {STATUS_OPTIONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField label="De" type="date" fullWidth size="small" value={accountDateFrom}
                  onChange={(e) => setAccountDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField label="Até" type="date" fullWidth size="small" value={accountDateTo}
                  onChange={(e) => setAccountDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button variant="contained" size="small" onClick={handleAccountSearch} disabled={accountsLoading}>Filtrar</Button>
                  <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)}
                    sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}>Nova Conta</Button>
                  <IconButton size="small" onClick={loadAccounts} disabled={accountsLoading}><RefreshIcon fontSize="small" /></IconButton>
                </Stack>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 0 }}>
              {/* Table area */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {accountsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                ) : (
                  <>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f9fafb' }}>
                            <TableCell sx={{ fontWeight: 700 }}>Situação</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Fornecedor / Descrição</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Valor</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Pago</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Vencimento</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>NF</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {accounts.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                <Typography color="text.secondary" sx={{ py: 3 }}>Nenhuma conta encontrada</Typography>
                              </TableCell>
                            </TableRow>
                          ) : accounts.map((acc) => {
                            const isSelected = selectedAccount?.id === acc.id;
                            return (
                              <TableRow key={acc.id} hover selected={isSelected}
                                onClick={() => handleSelectRow(acc)}
                                sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: '#f0fdf4' } }}>
                                <TableCell>
                                  <Chip label={getStatusLabel(acc.status)} size="small" color={getStatusColor(acc.status)}
                                    sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                </TableCell>
                                <TableCell>
                                  {acc.supplier_name && (
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{acc.supplier_name}</Typography>
                                  )}
                                  <Typography variant="body2" sx={{ fontWeight: acc.supplier_name ? 400 : 500, color: acc.supplier_name ? 'text.secondary' : 'text.primary', fontSize: '0.8rem' }}>
                                    {acc.description}
                                  </Typography>
                                  {acc.installment_total && acc.installment_total > 1 && (
                                    <Typography variant="caption" color="text.secondary">
                                      Parcela {acc.installment_number}/{acc.installment_total}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main', fontSize: '0.8rem' }}>
                                    {formatCurrency(acc.amount)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main', fontSize: '0.8rem' }}>
                                    {formatCurrency(acc.amount_paid || 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{formatDate(acc.due_date)}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    {resolvePayableNfeNumber(acc) || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    <Tooltip title="Ver detalhes">
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSelectRow(acc); setDrawerOpen(true); }}>
                                        <ViewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Editar">
                                      <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openEditAccount(acc); }}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5, borderTop: '1px solid #e5e7eb' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Total: {formatCurrency(summaryTotals.total)} &nbsp;|&nbsp; Pago: {formatCurrency(summaryTotals.paid)}
                      </Typography>
                    </Box>
                    <TablePagination component="div" count={accountsTotal} page={accountsPage}
                      onPageChange={(_, p) => setAccountsPage(p)} rowsPerPage={accountsRowsPerPage}
                      onRowsPerPageChange={(e) => { setAccountsRowsPerPage(parseInt(e.target.value, 10)); setAccountsPage(0); }}
                      rowsPerPageOptions={[5, 10, 25, 50]} labelRowsPerPage="Por página"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`} />
                  </>
                )}
              </Box>

              {/* Desktop sidebar */}
              {!isMobile && selectedAccount && (
                <Box sx={{ width: 240, borderLeft: '1px solid #e5e7eb', pl: 1, ml: 1, flexShrink: 0 }}>
                  <Box sx={{ p: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>
                      {selectedAccount.supplier_name || selectedAccount.description}
                    </Typography>
                    {resolvePayableNfeNumber(selectedAccount) && (
                      <Typography variant="caption" color="text.secondary">NF: {resolvePayableNfeNumber(selectedAccount)}</Typography>
                    )}
                    {selectedAccount.installment_total && selectedAccount.installment_total > 1 && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Parcela: {selectedAccount.installment_number}/{selectedAccount.installment_total}
                      </Typography>
                    )}
                  </Box>
                  <List dense disablePadding>
                    {sidebarActions.map((a, i) => (
                      <ListItemButton key={i} onClick={a.action} sx={{ py: 0.7 }}>
                        <ListItemIcon sx={{ minWidth: 32, color: (a as any).color || 'inherit' }}>{a.icon}</ListItemIcon>
                        <ListItemText primary={a.label} primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 500, color: (a as any).color || 'inherit' }} />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          </Box>
        </TabPanel>

        {/* ── Tab 1: Despesas Fixas ── */}
        <TabPanel value={tabIndex} index={1}>
          <Box sx={{ px: 2 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openFixedDialog()}>Nova Despesa Fixa</Button>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadFixedExpenses} disabled={fixedLoading}>Atualizar</Button>
            </Box>
            {fixedLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Descrição</TableCell><TableCell>Categoria</TableCell><TableCell align="right">Valor</TableCell>
                    <TableCell>Frequência</TableCell><TableCell align="center">Dia Venc.</TableCell><TableCell>Fornecedor</TableCell>
                    <TableCell align="center">Ativo</TableCell><TableCell align="center">Ações</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {fixedExpenses.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center"><Typography color="text.secondary" sx={{ py: 3 }}>Nenhuma despesa fixa</Typography></TableCell></TableRow>
                    ) : fixedExpenses.map((exp) => (
                      <TableRow key={exp.id} hover>
                        <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{exp.description}</Typography></TableCell>
                        <TableCell>{getCategoryLabel(exp.category)}</TableCell>
                        <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>{formatCurrency(exp.amount)}</Typography></TableCell>
                        <TableCell>{getFrequencyLabel(exp.frequency)}</TableCell>
                        <TableCell align="center">{exp.due_day}</TableCell>
                        <TableCell>{exp.supplier_name || '-'}</TableCell>
                        <TableCell align="center"><Chip label={exp.is_active ? 'Ativo' : 'Inativo'} size="small" color={exp.is_active ? 'success' : 'default'} /></TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="primary" onClick={() => openFixedDialog(exp)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteFixed(exp.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* ═══ WIZARD: Nova Conta a Pagar ═══ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={wizardOpen} onClose={() => setWizardOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, bgcolor: '#16a34a', color: '#fff' }}>
          Nova Conta a Pagar
          <IconButton onClick={() => setWizardOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Escolha como deseja incluir a nova conta a pagar:
          </Typography>
          <Stack spacing={2}>
            <Card variant="outlined" sx={{ borderColor: '#16a34a', borderWidth: 2 }}>
              <CardActionArea onClick={() => openNewAccount('nfe')} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <NFeIcon sx={{ fontSize: 40, color: '#16a34a' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#16a34a' }}>Nota Fiscal Eletrônica</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Registre a partir de uma NF-e com informações do fornecedor, itens e impostos
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
            <Card variant="outlined" sx={{ borderColor: '#16a34a', borderWidth: 2 }}>
              <CardActionArea onClick={() => openNewAccount('boleto')} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <BarcodeIcon sx={{ fontSize: 40, color: '#16a34a' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#16a34a' }}>Código de Barras</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Boletos bancários, contas de consumo, impostos e tributos
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
            <Card variant="outlined" sx={{ borderColor: '#16a34a', borderWidth: 2 }}>
              <CardActionArea onClick={() => openNewAccount('manual')} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ManualIcon sx={{ fontSize: 40, color: '#16a34a' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#16a34a' }}>Inserção Manual</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Digite manualmente todos os dados da conta a pagar
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* ═══ DIALOG: Conta a Pagar (Detalhes com Abas) ═══ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}>
        <Box sx={{ bgcolor: '#16a34a', px: 3, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {entryMode === 'nfe' && <NFeIcon sx={{ color: '#fff' }} />}
            {entryMode === 'boleto' && <BarcodeIcon sx={{ color: '#fff' }} />}
            {entryMode === 'manual' && <ManualIcon sx={{ color: '#fff' }} />}
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                {editingAccount ? 'Editar Conta a Pagar' : 'Nova Conta a Pagar'}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.72rem' }}>
                {entryMode === 'nfe' && 'Via Nota Fiscal Eletrônica'}
                {entryMode === 'boleto' && 'Via Boleto / Código de Barras'}
                {entryMode === 'manual' && 'Inserção Manual'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setAccountDialogOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          <Tabs value={detailTabIndex} onChange={(_, n) => setDetailTabIndex(n)}
            variant="scrollable" scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: '#fafafa' }}>
            <Tab label="Detalhes" />
            <Tab label="Parcelas" />
            <Tab label="Departamentos" />
            <Tab label="Pagamentos" />
            <Tab label="Anexos" />
          </Tabs>

          {/* Tab 0 - Detalhes (MODE-SPECIFIC) */}
          <TabPanel value={detailTabIndex} index={0}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>

                {/* ── NF-e MODE: NF-e first, then supplier, then account ── */}
                {entryMode === 'nfe' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ mb: 1 }}><Chip label="Nota Fiscal Eletrônica" size="small" icon={<NFeIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600, bgcolor: '#dcfce7' }} /></Divider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Número da NF-e *" fullWidth size="small" value={accountForm.nfe_number}
                        onChange={(e) => setAccountForm({ ...accountForm, nfe_number: e.target.value })}
                        placeholder="Ex: 001234"
                        helperText="Número da nota fiscal recebida" />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField label="Chave de Acesso NF-e (44 dígitos) *" fullWidth size="small" value={accountForm.nfe_key}
                        onChange={(e) => setAccountForm({ ...accountForm, nfe_key: e.target.value.replace(/\D/g, '') })}
                        inputProps={{ maxLength: 44 }}
                        helperText={
                          nfeLookupError ||
                          `${(accountForm.nfe_key || '').length}/44 dígitos` +
                          (nfeLookupLoading ? ' · consultando nota na Sefaz (via API)…' : ' · dados preenchidos automaticamente ao completar')
                        }
                        FormHelperTextProps={{
                          sx: nfeLookupError ? { color: 'error.main' } : undefined,
                        }}
                        InputProps={{
                          endAdornment: nfeLookupLoading ? (
                            <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
                          ) : undefined,
                        }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}><Chip label="Fornecedor / Emitente" size="small" icon={<SupplierIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600 }} /></Divider>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField label="Razão Social do Fornecedor *" fullWidth size="small" value={accountForm.supplier_name}
                        onChange={(e) => setAccountForm({ ...accountForm, supplier_name: e.target.value })}
                        helperText="Emitente da NF-e" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="CNPJ do Fornecedor *" fullWidth size="small" value={accountForm.supplier_cnpj}
                        onChange={(e) => setAccountForm({ ...accountForm, supplier_cnpj: e.target.value })}
                        placeholder="00.000.000/0001-00" />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}><Chip label="Valores e Vencimento" size="small" icon={<PaymentIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600 }} /></Divider>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Descrição dos Itens / Produtos *" fullWidth size="small" required value={accountForm.description}
                        onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })}
                        helperText="Descreva os produtos/serviços da nota" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Valor Total da NF-e *" fullWidth size="small" required type="number" value={accountForm.amount}
                        onChange={(e) => setAccountForm({ ...accountForm, amount: e.target.value })}
                        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Vencimento *" fullWidth size="small" required type="date" value={accountForm.due_date}
                        onChange={(e) => setAccountForm({ ...accountForm, due_date: e.target.value })} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small"><InputLabel>Forma de Pagamento</InputLabel>
                        <Select value={accountForm.payment_method} onChange={(e) => setAccountForm({ ...accountForm, payment_method: e.target.value })} label="Forma de Pagamento">
                          {PAYMENT_METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {/* ── BOLETO MODE: Barcode first, then payer info, then details ── */}
                {entryMode === 'boleto' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ mb: 1 }}><Chip label="Dados do Boleto / Conta" size="small" icon={<BarcodeIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600, bgcolor: '#dbeafe' }} /></Divider>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Linha Digitável / Código de Barras *" fullWidth size="small"
                        value={accountForm.barcode}
                        onChange={(e) => setAccountForm({ ...accountForm, barcode: e.target.value })}
                        placeholder="00000.00000 00000.000000 00000.000000 0 00000000000000"
                        helperText="Cole aqui a linha digitável do boleto ou conta de consumo"
                        InputProps={{ startAdornment: <InputAdornment position="start"><BarcodeIcon sx={{ color: '#6b7280', fontSize: 20 }} /></InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Descrição / Referência *" fullWidth size="small" required value={accountForm.description}
                        onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })}
                        placeholder="Ex: Conta de Luz - Ref. Março/2026"
                        helperText="Descreva a conta (ex: nome da concessionária, referência do mês)" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Valor *" fullWidth size="small" required type="number" value={accountForm.amount}
                        onChange={(e) => setAccountForm({ ...accountForm, amount: e.target.value })}
                        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Data de Vencimento *" fullWidth size="small" required type="date" value={accountForm.due_date}
                        onChange={(e) => setAccountForm({ ...accountForm, due_date: e.target.value })} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small"><InputLabel>Categoria</InputLabel>
                        <Select value={accountForm.category} onChange={(e) => setAccountForm({ ...accountForm, category: e.target.value })} label="Categoria">
                          {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}><Chip label="Beneficiário (Opcional)" size="small" icon={<SupplierIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600 }} /></Divider>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField label="Beneficiário / Cedente" fullWidth size="small" value={accountForm.supplier_name}
                        onChange={(e) => setAccountForm({ ...accountForm, supplier_name: e.target.value })}
                        placeholder="Ex: CPFL Energia, Vivo S.A." />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="CNPJ / CPF" fullWidth size="small" value={accountForm.supplier_cnpj}
                        onChange={(e) => setAccountForm({ ...accountForm, supplier_cnpj: e.target.value })} />
                    </Grid>
                  </>
                )}

                {/* ── MANUAL MODE: Full form with all fields ── */}
                {entryMode === 'manual' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ mb: 1 }}><Chip label="Fornecedor" size="small" icon={<SupplierIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600 }} /></Divider>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField label="Favorecido / Fornecedor" fullWidth size="small" value={accountForm.supplier_name}
                        onChange={(e) => setAccountForm({ ...accountForm, supplier_name: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="CNPJ / CPF" fullWidth size="small" value={accountForm.supplier_cnpj}
                        onChange={(e) => setAccountForm({ ...accountForm, supplier_cnpj: e.target.value })} />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}><Chip label="Dados da Conta" size="small" icon={<PaymentIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600 }} /></Divider>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Descrição *" fullWidth size="small" required value={accountForm.description}
                        onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Valor Total *" fullWidth size="small" required type="number" value={accountForm.amount}
                        onChange={(e) => setAccountForm({ ...accountForm, amount: e.target.value })}
                        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Vencimento *" fullWidth size="small" required type="date" value={accountForm.due_date}
                        onChange={(e) => setAccountForm({ ...accountForm, due_date: e.target.value })} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small"><InputLabel>Método de Pagamento</InputLabel>
                        <Select value={accountForm.payment_method} onChange={(e) => setAccountForm({ ...accountForm, payment_method: e.target.value })} label="Método de Pagamento">
                          {PAYMENT_METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}><Chip label="Nota Fiscal (Opcional)" size="small" icon={<NFeIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600 }} /></Divider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Número da NF-e" fullWidth size="small" value={accountForm.nfe_number}
                        onChange={(e) => setAccountForm({ ...accountForm, nfe_number: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField label="Chave de Acesso NF-e (44 dígitos)" fullWidth size="small" value={accountForm.nfe_key}
                        onChange={(e) => setAccountForm({ ...accountForm, nfe_key: e.target.value.replace(/\D/g, '') })}
                        inputProps={{ maxLength: 44 }} />
                    </Grid>
                  </>
                )}

                {/* ── Common classification section for all modes ── */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}><Chip label="Classificação" size="small" sx={{ fontWeight: 600 }} /></Divider>
                </Grid>
                {entryMode !== 'boleto' && (
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small"><InputLabel>Categoria</InputLabel>
                      <Select value={accountForm.category} onChange={(e) => setAccountForm({ ...accountForm, category: e.target.value })} label="Categoria">
                        {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sm={entryMode === 'boleto' ? 6 : 4}>
                  <TextField label="Conta Corrente (Banco)" fullWidth size="small" value={accountForm.bank_account}
                    onChange={(e) => setAccountForm({ ...accountForm, bank_account: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={entryMode === 'boleto' ? 6 : 4}>
                  <FormControl fullWidth size="small"><InputLabel>Status</InputLabel>
                    <Select value={accountForm.status} onChange={(e) => setAccountForm({ ...accountForm, status: e.target.value })} label="Status">
                      <MenuItem value="pendente">Pendente</MenuItem>
                      <MenuItem value="a_vencer">A Vencer</MenuItem>
                      <MenuItem value="pago">Pago</MenuItem>
                      <MenuItem value="atrasado">Atrasado</MenuItem>
                      <MenuItem value="cancelado">Cancelado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Comprador" fullWidth size="small" value={accountForm.buyer}
                    onChange={(e) => setAccountForm({ ...accountForm, buyer: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Projeto" fullWidth size="small" value={accountForm.project}
                    onChange={(e) => setAccountForm({ ...accountForm, project: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Observações / Dados Adicionais" fullWidth multiline rows={2} size="small" value={accountForm.notes}
                    onChange={(e) => setAccountForm({ ...accountForm, notes: e.target.value })} />
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          {/* Tab 1 - Parcelas */}
          <TabPanel value={detailTabIndex} index={1}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={4} sm={2}>
                  <TextField label="Nº de Parcelas" fullWidth size="small" type="number" value={accountForm.num_installments}
                    onChange={(e) => setAccountForm({ ...accountForm, num_installments: e.target.value })}
                    inputProps={{ min: 1, max: 120 }} />
                </Grid>
                <Grid item xs={8} sm={3}>
                  <Button variant="outlined" size="small" startIcon={<CalendarIcon />}
                    onClick={() => {
                      const total = parseFloat(accountForm.amount);
                      const count = parseInt(accountForm.num_installments) || 1;
                      const due = accountForm.due_date;
                      if (total && due) setInstallments(generateInstallments(total, count, due));
                    }}
                    sx={{ textTransform: 'none' }}>
                    Gerar Parcelas
                  </Button>
                </Grid>
              </Grid>
              {installments.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fef3c7' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Situação</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Parcela</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Vencimento</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Valor</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Percentual</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {installments.map((inst, i) => (
                        <TableRow key={i} sx={{ bgcolor: i === 0 ? '#fff7ed' : undefined }}>
                          <TableCell><Chip label="Previsão" size="small" sx={{ bgcolor: '#fef3c7', fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {String(inst.number).padStart(3, '0')}/{String(installments.length).padStart(3, '0')}
                          </TableCell>
                          <TableCell>
                            <TextField type="date" size="small" variant="standard" value={inst.due_date}
                              onChange={(e) => {
                                const copy = [...installments];
                                copy[i] = { ...copy[i], due_date: e.target.value };
                                setInstallments(copy);
                              }}
                              InputProps={{ disableUnderline: false }} sx={{ width: 140 }} />
                          </TableCell>
                          <TableCell align="right">
                            <TextField type="number" size="small" variant="standard" value={inst.amount}
                              onChange={(e) => {
                                const copy = [...installments];
                                copy[i] = { ...copy[i], amount: parseFloat(e.target.value) || 0 };
                                setInstallments(copy);
                              }}
                              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment>, disableUnderline: false }}
                              sx={{ width: 120 }} />
                          </TableCell>
                          <TableCell align="right">{inst.percentage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Defina o valor total, vencimento e número de parcelas, depois clique em "Gerar Parcelas".
                </Alert>
              )}
            </Box>
          </TabPanel>

          {/* Tab 2 - Departamentos (Centro de Custo) */}
          <TabPanel value={detailTabIndex} index={2}>
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Distribuição por Departamento
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Centro de Custo</InputLabel>
                <Select value={accountForm.cost_center} onChange={(e) => setAccountForm({ ...accountForm, cost_center: e.target.value })} label="Centro de Custo">
                  {COST_CENTERS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fef3c7' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Departamento</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Valor Distribuído</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>% da Distribuição</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {COST_CENTERS.map((cc) => {
                      const isActive = accountForm.cost_center === cc.value;
                      const total = parseFloat(accountForm.amount) || 0;
                      return (
                        <TableRow key={cc.value} sx={{ bgcolor: isActive ? '#fff7ed' : undefined }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 16, height: 16, borderRadius: '3px', border: '2px solid', borderColor: isActive ? '#16a34a' : '#d1d5db', bgcolor: isActive ? '#16a34a' : 'transparent' }} />
                              {cc.label}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(isActive ? total : 0)}</TableCell>
                          <TableCell align="right">{isActive ? '100%' : '0%'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* Tab 3 - Pagamentos */}
          <TabPanel value={detailTabIndex} index={3}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Total já Pago da Conta</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#16a34a' }}>
                      {formatCurrency(editingAccount?.amount_paid || 0)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Total de Descontos</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>R$ 0,00</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Juros e Multas</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>R$ 0,00</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0fdf4' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>$ A Pagar da Conta</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#dc2626' }}>
                      {formatCurrency((parseFloat(accountForm.amount) || 0) - (editingAccount?.amount_paid || 0))}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>Pagamentos Realizados</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Valor Pago</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Desconto</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Juros</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Multa</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Banco</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Observação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(!editingAccount?.payments || editingAccount.payments.length === 0) ? (
                      <TableRow><TableCell colSpan={7} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>Nenhum pagamento registrado</Typography>
                      </TableCell></TableRow>
                    ) : editingAccount.payments.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>{formatDate(p.date)}</TableCell>
                        <TableCell align="right">{formatCurrency(p.amount)}</TableCell>
                        <TableCell align="right">{formatCurrency(p.discount)}</TableCell>
                        <TableCell align="right">{formatCurrency(p.interest)}</TableCell>
                        <TableCell align="right">{formatCurrency(p.penalty)}</TableCell>
                        <TableCell>{p.bank || '-'}</TableCell>
                        <TableCell>{p.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* Tab 4 - Anexos (Funcional com Upload R2) */}
          <TabPanel value={detailTabIndex} index={4}>
            <Box sx={{ p: 3 }}>
              {!editingAccount ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Salve a conta primeiro para poder anexar arquivos.
                </Alert>
              ) : (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.xml,.txt,.zip"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files?.length) {
                        handleUploadAttachment(e.target.files);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: '2px dashed',
                      borderColor: isDragging ? '#16a34a' : '#d1d5db',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: isDragging ? '#f0fdf4' : '#fafafa',
                      transition: 'all 0.2s ease',
                      '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' },
                      mb: 2,
                    }}
                  >
                    <UploadIcon sx={{ fontSize: 48, color: isDragging ? '#16a34a' : '#94a3b8', mb: 1 }} />
                    <Typography sx={{ fontWeight: 600, color: isDragging ? '#16a34a' : '#374151' }}>
                      {isDragging ? 'Solte o arquivo aqui' : 'Arraste arquivos aqui ou clique para selecionar'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      PDF, imagens, planilhas, XML, documentos — até 25 MB por arquivo
                    </Typography>
                  </Box>

                  {uploadingFile && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: '#16a34a' } }} />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Enviando... {uploadProgress}%
                      </Typography>
                    </Box>
                  )}

                  {attachments.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f9fafb' }}>
                            <TableCell sx={{ fontWeight: 700, width: 40 }}></TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Arquivo</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Tamanho</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {attachments.map((att, i) => (
                            <TableRow key={att.id || i} hover>
                              <TableCell>{getFileIcon(att.type)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>{att.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{att.type}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {att.size ? formatFileSize(att.size) : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{formatDate(att.uploaded_at)}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                  <Tooltip title="Baixar">
                                    <IconButton size="small" onClick={() => handleDownloadAttachment(att)}
                                      disabled={!att.url && !att.file_url}>
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Excluir">
                                    <IconButton size="small" color="error" onClick={() => handleDeleteAttachment(att)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhum anexo ainda. Anexe comprovantes, DANFE, boletos, recibos, XML da NF-e, etc.
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </TabPanel>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
          <Button onClick={() => setAccountDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveAccount}
            sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, textTransform: 'none', fontWeight: 600 }}>
            {editingAccount ? 'Salvar Alterações' : 'Incluir no Contas a Pagar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ DIALOG: Pagar Agora ═══ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={payDialogOpen} onClose={() => setPayDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PayNowIcon sx={{ color: '#16a34a' }} /> Registrar Pagamento
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Ao registrar o pagamento, o status será alterado para <strong>PAGO</strong>.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Data do Pagamento" fullWidth size="small" type="date" value={payForm.date}
                onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Valor Pago *" fullWidth size="small" type="number" required value={payForm.amount}
                onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Desconto" fullWidth size="small" type="number" value={payForm.discount}
                onChange={(e) => setPayForm({ ...payForm, discount: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Juros" fullWidth size="small" type="number" value={payForm.interest}
                onChange={(e) => setPayForm({ ...payForm, interest: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Multa" fullWidth size="small" type="number" value={payForm.penalty}
                onChange={(e) => setPayForm({ ...payForm, penalty: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Banco / Conta" fullWidth size="small" value={payForm.bank}
                onChange={(e) => setPayForm({ ...payForm, bank: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Observação" fullWidth size="small" multiline rows={2} value={payForm.notes}
                onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPayDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handlePayNow}
            sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, textTransform: 'none', fontWeight: 600 }}>
            Registrar Pagamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ DIALOG: Registrar Desconto ═══ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={discountDialogOpen} onClose={() => setDiscountDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Registrar Desconto</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Valor do Desconto" fullWidth type="number" size="small" value={discountForm.amount}
              onChange={(e) => setDiscountForm({ ...discountForm, amount: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            <TextField label="Observação" fullWidth multiline rows={2} size="small" value={discountForm.notes}
              onChange={(e) => setDiscountForm({ ...discountForm, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscountDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleRegisterDiscount}>Registrar</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ DRAWER Mobile: Ações ═══ */}
      <Drawer anchor="bottom" open={isMobile && drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px 16px 0 0', maxHeight: '60vh' } }}>
        {selectedAccount && (
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{selectedAccount.supplier_name || selectedAccount.description}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {formatCurrency(selectedAccount.amount)} — Venc. {formatDate(selectedAccount.due_date)}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <List dense>
              {sidebarActions.map((a, i) => (
                <ListItemButton key={i} onClick={() => { a.action(); if (a.label !== 'Pagar Agora' && a.label !== 'Registrar Desconto' && a.label !== 'Editar') setDrawerOpen(false); }}>
                  <ListItemIcon sx={{ minWidth: 36, color: (a as any).color || 'inherit' }}>{a.icon}</ListItemIcon>
                  <ListItemText primary={a.label} primaryTypographyProps={{ fontWeight: 500, color: (a as any).color || 'inherit' }} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}
      </Drawer>

      {/* ═══ Fixed Expense Dialog ═══ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={fixedDialogOpen} onClose={() => setFixedDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingFixed ? 'Editar Despesa Fixa' : 'Nova Despesa Fixa'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField label="Descrição" fullWidth required value={fixedForm.description} onChange={(e) => setFixedForm({ ...fixedForm, description: e.target.value })} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth><InputLabel>Categoria</InputLabel>
                  <Select value={fixedForm.category} onChange={(e) => setFixedForm({ ...fixedForm, category: e.target.value })} label="Categoria">
                    {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Valor" fullWidth required type="number" value={fixedForm.amount}
                  onChange={(e) => setFixedForm({ ...fixedForm, amount: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth><InputLabel>Frequência</InputLabel>
                  <Select value={fixedForm.frequency} onChange={(e) => setFixedForm({ ...fixedForm, frequency: e.target.value })} label="Frequência">
                    {FREQUENCY_OPTIONS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Dia de Vencimento" fullWidth required type="number" value={fixedForm.due_day}
                  onChange={(e) => setFixedForm({ ...fixedForm, due_day: e.target.value })} inputProps={{ min: 1, max: 31 }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Fornecedor" fullWidth value={fixedForm.supplier_name}
                  onChange={(e) => setFixedForm({ ...fixedForm, supplier_name: e.target.value })} />
              </Grid>
            </Grid>
            <FormControlLabel control={<Switch checked={fixedForm.is_active} onChange={(e) => setFixedForm({ ...fixedForm, is_active: e.target.checked })} />} label="Ativo" />
            <TextField label="Notas" fullWidth multiline rows={3} value={fixedForm.notes}
              onChange={(e) => setFixedForm({ ...fixedForm, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFixedDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveFixed}>{editingFixed ? 'Atualizar' : 'Criar'}</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Snackbar ═══ */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountsPayable;
